import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { MUSEUM_STORAGE_KEY, defaultMuseumSettings, validateMuseumSettings, prepareMuseumExhibition } from '../../utils/museumSettings';
import MuseumPlayer from './MuseumPlayer';
import './Museum.css';

const MuseumExhibition = forwardRef(function MuseumExhibition({ show, onHide, projects, projectId, networkId, provider, onActiveChange,
  currentTokens, currentScript, currentCSS, currentDetails }, ref) {
  const [settings, setSettings] = useState(defaultMuseumSettings(projectId, networkId));
  const [prepared, setPrepared] = useState(null);
  const [playing, setPlaying] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [quickError, setQuickError] = useState('');
  const request = useRef({ id: 0 });
  const importInput = useRef(null);
  const enterFullscreen = () => {
    try {
      const enter = document.documentElement.requestFullscreen || document.documentElement.webkitRequestFullscreen;
      if (enter) { const result = enter.call(document.documentElement); if (result && result.catch) result.catch(() => {}); }
    } catch (e) { /* Windowed playback remains available. */ }
  };
  // The original button starts immediately from the already loaded project.
  // Optional saved configurations remain available through the separate gear.
  useImperativeHandle(ref, () => ({ startDefault() {
    const entries = (currentTokens || []).filter(t => t && t.transactionHash);
    if (!currentScript || !entries.length || !projects[projectId - 1]) {
      setQuickError('Load a project with available editions to start museum mode.'); return;
    }
    setQuickError(''); enterFullscreen();
    setPlaying({ settings: defaultMuseumSettings(projectId, networkId), entries,
      catalogue: { [projectId]: { project: projects[projectId - 1], script: currentScript, css: currentCSS, details: currentDetails } } });
    onActiveChange(true); onHide();
  } }));

  useEffect(() => {
    if (!show) return;
    let next = defaultMuseumSettings(projectId, networkId);
    try {
      const saved = localStorage.getItem(MUSEUM_STORAGE_KEY);
      if (saved) next = validateMuseumSettings(JSON.parse(saved), networkId);
    } catch (e) { /* A different network or unavailable storage starts a fresh selection. */ }
    setSettings(next);
    setError(''); setMessage(''); setPrepared(null);
    const cancellation = request.current;
    return () => { cancellation.id++; };
  }, [show, projectId, networkId]);

  const update = patch => {
    request.current.id++;
    setSettings(old => ({ ...old, ...patch }));
    setPrepared(null); setError(''); setMessage(''); setBusy(false);
  };
  const close = () => { request.current.id++; setBusy(false); onHide(); };
  const save = value => {
    try { localStorage.setItem(MUSEUM_STORAGE_KEY, JSON.stringify(value)); return true; }
    catch (e) { setError('This browser could not save the exhibition. Export a settings file to keep it.'); return false; }
  };
  const review = async () => {
    const id = ++request.current.id;
    setBusy(true); setError(''); setPrepared(null);
    try {
      const valid = validateMuseumSettings(settings, networkId);
      const result = await prepareMuseumExhibition(valid, projects, provider, text => {
        if (id === request.current.id) setMessage(text);
      }, () => id !== request.current.id);
      if (id !== request.current.id) return;
      setPrepared(result); setMessage('Selection ready. Review the editions below.'); save(valid);
    } catch (e) { if (id === request.current.id) { setError(e.message); setMessage(''); } }
    finally { if (id === request.current.id) setBusy(false); }
  };
  const start = () => {
    if (!prepared) return;
    if (settings.mode === 'projection') {
      try {
        localStorage.setItem('immutables.gallery.museum-import', JSON.stringify({
          version: 1, networkId: Number(networkId), name: settings.name, seconds: settings.seconds,
          tokens: prepared.entries.map(t => ({ tokenId: String(t.tokenId), projectId: t.projectId, transactionHash: t.transactionHash }))
        }));
        window.location.assign(process.env.PUBLIC_URL + '/immutablesArtGallery.html?mode=program&museum=1');
      } catch (e) { setError('The selection could not be passed to projection mapping. Allow local storage and try again.'); }
      return;
    }
    // Request while the click still carries browser activation, before rendering.
    enterFullscreen();
    setPlaying(prepared); onActiveChange(true); close();
  };
  const stop = useCallback(() => {
    setPlaying(null); onActiveChange(false);
    if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {});
  }, [onActiveChange]);
  const exportSettings = () => {
    try {
      const valid = validateMuseumSettings(settings, networkId);
      const url = URL.createObjectURL(new Blob([JSON.stringify(valid, null, 2)], { type: 'application/json' }));
      const link = document.createElement('a'); link.href = url; link.download = 'immutables-exhibition.json'; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setError(e.message); }
  };
  const importSettings = async event => {
    const file = event.target.files[0]; event.target.value = '';
    if (!file) return;
    try {
      if (file.size > 100000) throw new Error('Choose an exhibition settings file under 100 KB.');
      update(validateMuseumSettings(JSON.parse(await file.text()), networkId));
    } catch (e) { setError('Could not import settings. ' + e.message); }
  };

  return <>
    {quickError && <p className="museum-start-error" role="alert">{quickError}<button onClick={() => setQuickError('')} aria-label="Dismiss museum message">×</button></p>}
    <Modal show={show} onHide={close} size="lg" className="dock-plain museum-settings" scrollable aria-labelledby="museum-settings-title">
      <Modal.Header closeButton>
        <Modal.Title id="museum-settings-title" className="font-display">Museum mode</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="museum-intro">Optional settings for museum mode. By default, it fades between random editions and the current project’s grid every 30 seconds.</p>
        <div className="museum-modes" role="group" aria-label="Exhibition mode">
          <button type="button" aria-pressed={settings.mode === 'screen'} onClick={() => update({ mode: 'screen' })}>
            <strong>Museum mode</strong><span>The familiar edition and grid cycle, with your choices.</span>
          </button>
          <button type="button" aria-pressed={settings.mode === 'projection'} onClick={() => update({ mode: 'projection', order: settings.order === 'random' ? 'sequence' : settings.order })}>
            <strong>Projection mapping</strong><span>Multiple outputs, mapped surfaces and a timeline.</span>
          </button>
        </div>
        <label className="museum-field">Exhibition name
          <input value={settings.name} maxLength={120} onChange={e => update({ name: e.target.value })} />
        </label>
        <fieldset className="museum-projects" disabled={busy}>
          <legend>Works to include</legend>
          <p>Choose whole projects or enter edition numbers within each project. Use Order below to choose how they rotate.</p>
          {projects.map((project, index) => {
            if (!project) return null;
            const id = index + 1, rule = settings.projects.find(p => p.projectId === id);
            return <div className="museum-project" key={id}>
              <label className="museum-project-choice">
                <input type="checkbox" checked={!!rule} aria-label={'Include ' + project.name} onChange={e => update({ projects: e.target.checked
                  ? [...settings.projects, { projectId: id, editions: '' }]
                  : settings.projects.filter(p => p.projectId !== id) })} />
                <span><strong>{project.name}</strong><small>{project.artist} · {project.currentEditionId || 0} editions</small></span>
              </label>
              {rule && <label className="museum-editions">Edition numbers
                <input aria-label={project.name + ' edition numbers'} value={rule.editions} placeholder="All editions, or 1, 3, 7-12" onChange={e => update({ projects: settings.projects.map(p => p.projectId === id ? { ...p, editions: e.target.value } : p) })} />
              </label>}
            </div>;
          })}
        </fieldset>
        <label className="museum-field">Only editions held by these collection wallets <small>Optional</small>
          <textarea rows={2} value={settings.wallets} placeholder="0x… addresses, separated by commas or new lines" onChange={e => update({ wallets: e.target.value })} />
        </label>
        <p className="museum-help">Matches any listed wallet within your selected projects and editions. No wallet connection or transfer is required.
          {provider && provider.ownersCapturedAt ? ' Ownership comes from the archive dated ' + new Date(provider.ownersCapturedAt).toLocaleString() + '.' : ' Ownership is checked when you review the selection.'}
          {' '}The reviewed list stays fixed during playback; review again to include ownership changes.</p>
        <div className="museum-field-pair">
          <label className="museum-field">Seconds per edition
            <input aria-label="Seconds per edition" type="number" min={5} max={86400} value={settings.seconds} onChange={e => update({ seconds: e.target.value })} />
          </label>
          <label className="museum-field">Order
            <select aria-label="Edition order" value={settings.order} onChange={e => update({ order: e.target.value })}>
              {settings.mode === 'screen' && <option value="random">Random edition each cycle</option>}<option value="sequence">Project order, then edition number</option><option value="shuffle">Shuffle once, then repeat this order</option>
            </select>
          </label>
        </div>
        <p className="museum-help">For evolving works such as EoD, use longer durations or hold the current edition.</p>
        {settings.mode === 'screen' ? <>
          <label className="museum-check"><input type="checkbox" aria-label="Return to the project grid" checked={settings.showGrid} onChange={e => update({ showGrid: e.target.checked })} /> Return to the project grid between editions</label>
          {settings.showGrid && <label className="museum-field">Seconds on the grid
            <input aria-label="Seconds on the grid" type="number" min={2} max={86400} value={settings.gridSeconds} onChange={e => update({ gridSeconds: e.target.value })} />
          </label>}
          <p className="museum-help">The grid follows the project’s normal preview settings and shows only editions in your selection. Turn it off for uninterrupted single-edition display.</p>
          <label className="museum-field">Presentation style
            <select aria-label="Presentation style" value={settings.style} onChange={e => update({ style: e.target.value })}>
              <option value="black">Black surroundings</option><option value="white">White surroundings</option>
              <option value="artist">Each project’s artist CSS</option><option value="custom">Custom exhibition CSS</option>
            </select>
          </label>
          {settings.style === 'custom' && <label className="museum-field">Exhibition CSS
            <textarea aria-label="Exhibition CSS" rows={5} value={settings.customCSS} spellCheck={false} placeholder="body { background: #000; color: #fff; }" onChange={e => update({ customCSS: e.target.value })} />
            <small>Applies to the artwork stage. Existing project classes are available. Staff controls stay accessible.</small>
          </label>}
          <div className="museum-options">
            <label><input type="checkbox" checked={settings.labels} onChange={e => update({ labels: e.target.checked })} /> Show title, artist, edition and URL</label>
            <label><input type="checkbox" checked={settings.interaction} onChange={e => update({ interaction: e.target.checked })} /> Allow visitor interaction</label>
          </div>
        </> : <p className="museum-help">The mapping tool renders p5 projects live on a black field. Your reviewed selection becomes a new scene; existing scenes and calibrated surfaces are preserved. Adjust mapping, transitions and output layouts there. Single-screen CSS and labels do not carry over.</p>}
        <div className="museum-file-actions">
          <Button size="sm" variant="outline-secondary" onClick={() => update(defaultMuseumSettings(projectId, networkId))}>Restore defaults</Button>
          <Button size="sm" variant="outline-secondary" onClick={() => { try { if (save(validateMuseumSettings(settings, networkId))) setMessage('Saved in this browser.'); } catch (e) { setError(e.message); } }}>Save settings</Button>
          <Button size="sm" variant="outline-secondary" onClick={exportSettings}>Export settings</Button>
          <Button size="sm" variant="outline-secondary" onClick={() => importInput.current.click()}>Import settings</Button>
          <input ref={importInput} type="file" accept=".json,application/json" hidden onChange={importSettings} />
        </div>
        {error && <p className="museum-error" role="alert">{error}</p>}
        {message && <p className="museum-status" role="status">{message}</p>}
        {prepared && <section className="museum-review" aria-label="Reviewed selection">
          <h3>{prepared.entries.length} {prepared.entries.length === 1 ? 'edition' : 'editions'} ready</h3>
          <ol>{prepared.entries.map(t => <li key={t.projectId + ':' + t.tokenId}>{prepared.catalogue[t.projectId].project.name} #{t.editionId}</li>)}</ol>
        </section>}
      </Modal.Body>
      <Modal.Footer>
        <a href="https://immutables.art/docs/exhibiting" target="_blank" rel="noopener noreferrer">Exhibition guide</a>
        <Button variant="outline-secondary" onClick={close}>{busy ? 'Cancel preparation' : 'Close'}</Button>
        {prepared ? <Button variant="dark" onClick={start}>{settings.mode === 'projection' ? 'Open projection mapping' : 'Start museum mode'}</Button>
          : <Button variant="dark" disabled={busy} onClick={review}>{busy ? 'Preparing…' : 'Review selection'}</Button>}
      </Modal.Footer>
    </Modal>
    {playing && <MuseumPlayer exhibition={playing} onStop={stop} onSettings={() => { stop(); /* Open through the caller after stopping. */ onActiveChange(false, true); }} />}
  </>;
});

export default MuseumExhibition;
